import React from 'react';
import { OnboardingMilestone } from '../services/EmployeeService';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import {
  FaUser,
  FaIdCard,
  FaGraduationCap,
  FaFileContract,
  FaPaperPlane,
  FaFileSignature,
  FaShieldAlt,
  FaUserCheck,
  FaClipboardList,
  FaComments,
  FaBriefcase,
  FaPlay,
  FaTshirt,
} from 'react-icons/fa';

interface OnboardingProgressBarProps {
  milestones?: OnboardingMilestone[];
}

const DEFAULT_MILESTONES: OnboardingMilestone[] = [
  { step: 'invited', label: 'Invited', status: 'PENDING' },
  { step: 'personal_data', label: 'Personal Data', status: 'PENDING' },
  { step: 'documents', label: 'Documents', status: 'PENDING' },
  { step: 'contract_signed', label: 'Contract Signed', status: 'PENDING' },
  { step: 'active', label: 'Active', status: 'PENDING' },
];

const OnboardingProgressBar: React.FC<OnboardingProgressBarProps> = ({ milestones }) => {
  const steps = milestones && milestones.length > 0 ? milestones : DEFAULT_MILESTONES;

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const getIconForStep = (step: string) => {
    if (!step) return FaClipboardList;
    const stepLower = step.toLowerCase();

    if (stepLower === 'interview' || stepLower.includes('interview')) {
      return FaComments;
    }
    if (stepLower === 'probearbeiten' || stepLower.includes('probe') || stepLower.includes('trial')) {
      return FaBriefcase;
    }
    if (stepLower === 'vertrag' || stepLower.includes('vertrag') || stepLower.includes('contract')) {
      return FaFileContract;
    }
    if (stepLower === 'start' || stepLower.includes('start')) {
      return FaPlay;
    }
    if (stepLower.includes('dienstkleidung') || stepLower.includes('dienst') || stepLower.includes('asset')) {
      return FaTshirt;
    }
    if (stepLower.includes('personal_data') || stepLower.includes('personal data')) {
      return FaUser;
    }
    if (stepLower.includes('red_card') || stepLower.includes('rote karte') || stepLower.includes('infektionsschutz')) {
      return FaIdCard;
    }
    if (stepLower.includes('imma') || stepLower.includes('immatrikulation')) {
      return FaGraduationCap;
    }
    if (stepLower.includes('contract_sent') || stepLower.includes('contract sent')) {
      return FaPaperPlane;
    }
    if (stepLower.includes('signed')) {
      return FaFileSignature;
    }
    if (stepLower.includes('police') || stepLower.includes('führungszeugnis')) {
      return FaShieldAlt;
    }
    if (stepLower.includes('manager_freed') || stepLower.includes('manager freed') || stepLower.includes('active') || stepLower.includes('freed')) {
      return FaUserCheck;
    }
    return FaClipboardList;
  };

  return (
    <div
      className="hover-card"
      style={{
        ...ps.card,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <h2 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
        Onboarding Progress
      </h2>

      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'relative',
          }}
        >
          {steps.map((milestone, index) => {
            const isDone = milestone.status === 'DONE';
            const isPending = milestone.status === 'PENDING';
            const isSkipped = milestone.status === 'SKIPPED';
            const stepIdentifier = milestone.label || milestone.step;
            const StepIcon = getIconForStep(stepIdentifier);

            return (
              <div
                key={milestone.step}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {index > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 20,
                      right: '50%',
                      width: '100%',
                      height: 2,
                      background: isDone
                        ? colors.slate
                        : colors.borderLight,
                      zIndex: 0,
                    }}
                  />
                )}

                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    background: isDone
                      ? colors.slate
                      : isPending
                        ? colors.bgCard
                        : colors.bgMuted,
                    border: isDone ? 'none' : `2px solid ${isPending ? colors.border : colors.borderLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <StepIcon
                    size={16}
                    color={isDone ? '#fff' : isSkipped ? colors.textLight : colors.textLight}
                  />
                </div>

                <div
                  style={{
                    marginTop: 10,
                    textAlign: 'center',
                    fontSize: 12,
                    fontWeight: isDone ? 600 : 500,
                    color: isDone ? colors.textBody : colors.textLight,
                  }}
                >
                  {milestone.label}
                </div>

                {milestone.completed_at && (
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 11,
                      color: isDone ? colors.slate : colors.textLight,
                      textAlign: 'center',
                      fontWeight: 500,
                    }}
                  >
                    {formatDate(milestone.completed_at)}
                  </div>
                )}

                {isSkipped && (
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 11,
                      color: colors.textLight,
                      textAlign: 'center',
                      fontStyle: 'italic',
                    }}
                  >
                    Skipped
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OnboardingProgressBar;
